import { ChangeEvent, memo, useEffect, useRef, useState } from 'react'

import { TextField, TextFieldProps } from '@mui/material'

// Optional leading '-', digits, a single optional decimal point. Also matches the
// intermediate states you pass through while typing: '', '-', '10.', '-.', '.5'.
const NUMERIC = /^-?\d*\.?\d*$/

export type NumberFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number
  onChange: (value: number) => void
}

/**
 * Controlled numeric input that accepts ONLY a valid number (optional leading
 * `-`, digits, one decimal point) — rejecting things like `10-0349` — while
 * keeping intermediate states (`-`, `10.`) as you type. Emits NaN for
 * empty/partial input; the persist layer guards against saving NaN.
 */
const NumberFieldRaw = ({ value, onChange, ...props }: NumberFieldProps) => {
  const [text, setText] = useState(() => (Number.isFinite(value) ? String(value) : ''))
  // Last value we emitted, so an external change (e.g. form reset) re-syncs the
  // text but our own keystrokes don't clobber the in-progress string.
  const lastEmitted = useRef<number>(value)

  useEffect(() => {
    if (!Object.is(value, lastEmitted.current)) {
      lastEmitted.current = value
      setText(Number.isFinite(value) ? String(value) : '')
    }
  }, [value])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    if (raw !== '' && !NUMERIC.test(raw)) {
      return
    }
    setText(raw)
    const parsed = raw === '' ? NaN : Number(raw)
    lastEmitted.current = parsed
    onChange(parsed)
  }

  return (
    <TextField {...props} value={text} onChange={handleChange} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
  )
}

export const NumberField = memo(NumberFieldRaw)
